import {
  computed,
  onScopeDispose,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
} from 'vue'

import { anomalyApi } from '../api/AnomalyApi'
import { LoadAnomaliesFlow } from '../flows/LoadAnomaliesFlow'
import type { MetricIndexFallback } from '../service/adaptAnomaly'
import { useAnomalyStore, type AnomalyLoadStatus } from '../stores/anomalyStore'
import { reportAnomalyMetricIndexFallback } from '../telemetry/anomalyTelemetry'

import { useAnomalyCatalogContext } from './useAnomalyCatalogContext'
import { useAnomalyFormat } from './useAnomalyFormat'

import { useWorkspace } from '@/features/account'
import { mapColumns, useMetricCatalog } from '@/features/reports'
import type { ApiError } from '@/shared/http'

interface AnomalyLoadFlow {
  execute: (workspaceIds: readonly number[], signal?: AbortSignal) => Promise<void>
}

export interface UseAnomaliesOptions {
  workspaceIds?: MaybeRefOrGetter<readonly number[]>
  flow?: AnomalyLoadFlow
  onMetricIndexFallback?: (event: MetricIndexFallback) => void
}

export interface UseAnomalies {
  anomalies: ComputedRef<ReturnType<typeof useAnomalyStore>['records']>
  needsAttention: ComputedRef<ReturnType<typeof useAnomalyStore>['needsAttention']>
  informational: ComputedRef<ReturnType<typeof useAnomalyStore>['informational']>
  state: ComputedRef<AnomalyLoadStatus>
  error: ComputedRef<ApiError | null>
  load: () => Promise<void>
  reset: () => void
}

export function useAnomalies(options: UseAnomaliesOptions = {}): UseAnomalies {
  const workspace = options.workspaceIds ? null : useWorkspace()
  const store = useAnomalyStore()
  const flow = options.flow ?? createDefaultFlow()
  let controller: AbortController | null = null

  const workspaceIds = computed(() => {
    const explicit = options.workspaceIds ? toValue(options.workspaceIds) : undefined
    const current = numericId(workspace?.workspaceId.value)
    return normalizeIds(explicit ?? (current === undefined ? [] : [current]))
  })

  async function load(): Promise<void> {
    controller?.abort()
    const ids = workspaceIds.value
    if (ids.length === 0) {
      store.reset()
      return
    }
    controller = new AbortController()
    await flow.execute(ids, controller.signal)
  }

  function reset(): void {
    controller?.abort()
    controller = null
    store.reset()
  }

  watch(
    workspaceIds,
    () => {
      reset()
      void load()
    },
    { immediate: true },
  )
  onScopeDispose(() => controller?.abort())

  return {
    anomalies: computed(() => store.records),
    needsAttention: computed(() => store.needsAttention),
    informational: computed(() => store.informational),
    state: computed(() => store.status),
    error: computed(() => store.error),
    load,
    reset,
  }

  function createDefaultFlow(): AnomalyLoadFlow {
    const catalog = useMetricCatalog()
    const catalogContext = useAnomalyCatalogContext()
    const anomalyFormat = useAnomalyFormat()
    return new LoadAnomaliesFlow({
      api: anomalyApi,
      store,
      context: {
        ...catalogContext,
        formatMetric: anomalyFormat.formatMetric,
        prepareMetrics: (fields) => {
          mapColumns(fields, { getColumn: catalog.getColumn })
        },
        now: Date.now,
        onMetricIndexFallback: options.onMetricIndexFallback ?? reportAnomalyMetricIndexFallback,
      },
    })
  }
}

function numericId(value: string | number | undefined): number | undefined {
  if (value === undefined || (typeof value === 'string' && value.trim() === '')) return undefined
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : undefined
}

function normalizeIds(values: readonly number[]): number[] {
  return [...new Set(values.filter((value) => Number.isFinite(value)))]
}
